// janetObjectPath — pure reducer logic for Janet's persistent object path
// (Intentional Propulsion Slice 6, issue #169). Represents WHERE Aaron and
// Janet are in the object/path (Property → Deal / Expression / Artifact /
// Person…) as a small ordered stack of references — never a copy of any
// canonical record, only { type, id, label, path }. No second truth store:
// this is pure navigation continuity, the same spirit as zoomMemory.js's
// restorative zoom-out, just one altitude wider (across canonical domains,
// not within one board).
//
// Three operations cover every lateral move the certification asks for:
//   - visit(stack, ref)  — a canonical page announces itself on mount.
//       Idempotent: already-current → no-op; an ancestor already on the
//       stack → drop everything above it (a real "return"); otherwise this
//       is a fresh anchor and the stack resets to just this ref (so ordinary
//       browsing to an unrelated object never grows a stale trail).
//   - push(stack, ref)   — a lateral door calls this BEFORE navigating away,
//       so the destination's own visit() finds itself already current and
//       does not reset the trail. Caps at MAX_STACK so this can never grow
//       unbounded across a long session.
//   - pop(stack)         — "go back to the project": drop the current frame,
//       land on whatever is now on top.
//
// Staleness (isFresh) bounds how long a "← Back to X" affordance may show
// after the session goes quiet — the same discipline zoomMemory.js's
// MAX_AGE_MS uses, so a week-old context never leaks back in.

export const MAX_STACK = 6
export const MAX_AGE_MS = 45 * 60 * 1000

export function isValidRef(ref) {
  return !!(ref && ref.type && ref.id != null && ref.path)
}

export function sameRef(a, b) {
  return !!a && !!b && a.type === b.type && String(a.id) === String(b.id)
}

export function reduceVisit(stack, ref) {
  if (!isValidRef(ref)) return stack
  const list = Array.isArray(stack) ? stack : []
  if (!list.length) return [ref] // nothing to compare against — a fresh anchor
  const i = list.findIndex((x) => sameRef(x, ref))
  if (i === list.length - 1) return list // already the current frame
  if (i >= 0) return list.slice(0, i + 1) // returned to an ancestor — drop what's above it
  return [ref] // a fresh anchor — start a new path, don't accumulate a stale trail
}

export function reducePush(stack, ref) {
  if (!isValidRef(ref)) return stack
  const list = Array.isArray(stack) ? stack : []
  if (list.length && sameRef(list[list.length - 1], ref)) return list
  return [...list, ref].slice(-MAX_STACK)
}

export function reducePop(stack) {
  const list = Array.isArray(stack) ? stack : []
  return list.length > 1 ? list.slice(0, -1) : list
}

export function currentOf(stack) {
  const list = Array.isArray(stack) ? stack : []
  return list.length ? list[list.length - 1] : null
}

/** The frame a "← Back to X" affordance would land on, or null if there is none. */
export function returnTargetOf(stack) {
  const list = Array.isArray(stack) ? stack : []
  return list.length > 1 ? list[list.length - 2] : null
}

export function isFresh(ts, now = Date.now()) {
  return typeof ts === 'number' && now - ts <= MAX_AGE_MS
}

// --- Root vs. contextual "open" -------------------------------------------
//
// A context card (JanetChatPanel.jsx's OpenContextCard) can open a resolved
// object two different ways, and both already have a primitive above:
//   - A genuine contextual lateral move — a door clicked from inside an
//     already-open room, or a follow-on command like "show me the deal" /
//     "back to the project" while a trail is active — EXTENDS the current
//     trail. That's reducePush (via pushObject): idempotent when the
//     destination is already current, so the destination page's own
//     reduceVisit() call on mount finds itself already on top and the trail
//     beneath it survives as returnTarget.
//   - A fresh TOP-LEVEL natural-language entry (Janet's deterministic
//     property-lookup bridge, propertyContextBridge.js — resolved from a
//     message typed cold, possibly with nothing to do with whatever was on
//     screen before) establishes a NEW root — reduceVisit (via visitObject):
//     a ref unrelated to anything on the stack resets to a fresh
//     single-item stack, discarding a stale prior trail rather than
//     appending the new object onto it (an ancestor already on the stack
//     trims back to it instead — the same "real return" reduceVisit always
//     had). Calling pushObject here instead is exactly the bug this fixes:
//     pushObject pre-seeds the destination's own idempotent reduceVisit
//     check, so the stale trail beneath it never gets the chance to reset.
//
// objectOpenPrimitive just names that choice as one pure, tested decision
// instead of an inline conditional repeated at each call site. It does not
// touch the stack itself — the caller still calls the existing
// pushObject/visitObject context methods with the result.
export function objectOpenPrimitive(payload) {
  return payload && payload.root ? 'visit' : 'push'
}

// --- Cross-tab return-context carry ---------------------------------------
//
// Every Console/Studio doorway opens its destination with
// `window.open(dest, '_blank', 'noopener')` (consoleBridge.js openConsolePath)
// so the OS tab stays put. Per the HTML spec, a new browsing context only
// inherits the opener's sessionStorage when the opener reference is kept —
// 'noopener' puts the new tab in its OWN browsing-context group, so it boots
// with a genuinely EMPTY sessionStorage. That's exactly what
// JanetObjectContext's stack persists to, so a frame pushed in the original
// tab never reaches the new one — the return bridge silently has nothing to
// show. withCarryParam/decodeCarryRef move exactly ONE ref (what to bridge
// back to) through the URL instead, which is the one channel that DOES cross
// a noopener boundary. Bounded to a single ref — never the whole stack — so
// nothing beyond "what to return to" ever sits in the address bar or
// browser history.

export const CARRY_PARAM = 'janet_return'

// encodeCarryRef's output is meant to be appended LITERALLY as a query
// string value (withCarryParam does exactly that). decodeCarryRef, in turn,
// expects the value exactly as URLSearchParams.get() hands it back — which
// the browser has ALREADY percent-decoded once. Do not add a second
// decodeURIComponent here: a label containing a literal '%' (e.g. "50% off")
// would round-trip fine through one decode but throw or mis-decode on a
// second one.

export function encodeCarryRef(ref) {
  if (!isValidRef(ref)) return null
  try {
    return encodeURIComponent(JSON.stringify({ type: ref.type, id: ref.id, label: ref.label, path: ref.path }))
  } catch {
    return null
  }
}

export function decodeCarryRef(raw) {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return isValidRef(parsed) ? parsed : null
  } catch {
    return null
  }
}

// --- Cross-tab carry, extended to a bounded ANCESTOR CHAIN -----------------
//
// A single carried ref (above) is enough when the doorway's own tab has
// nothing beneath the object it's bridging from. It is NOT enough when that
// tab's stack already has an ancestor under the current object (Property →
// Brainstorm door on an expression row, without ever visiting the Expression
// page itself in this tab) — carrying only the immediate ref would land the
// new tab's eventual "→ Expression" return with no further "→ Property"
// beneath it, silently truncating the trail at the noopener boundary
// (2026-09-29 repair, issue #170).
//
// encodeCarryChain/decodeCarryChain carry a small, bounded ORDERED chain
// (oldest/ancestor first, immediate/bridged-from object last) through the
// SAME CARRY_PARAM — never the whole stack (MAX_STACK), just MAX_CARRY_DEPTH,
// which is exactly what "one ancestor beneath the immediate object" needs.
// decodeCarryChain also accepts a bare single-ref object (the shape
// encodeCarryRef produces) so it can decode either encoding transparently —
// no flag day, no second query param.
export const MAX_CARRY_DEPTH = 2

/** The bounded chain a doorway should carry: whatever ancestor the CURRENT
 * tab's live stack already has beneath `ref`, plus `ref` itself — built from
 * the stack BEFORE pushObject(ref) is called (pushObject is an async state
 * update; this reads the synchronous value the caller already has). */
export function buildCarryChain(stack, ref) {
  if (!isValidRef(ref)) return null
  const list = Array.isArray(stack) ? stack : []
  // A doorway may be invoked from a page whose destination ref is already
  // the current object (Property → Expression page → Studio). Appending it
  // again would produce [..., Expression, Expression]; bounding that to two
  // frames drops the Property ancestor. Match reducePush's idempotence:
  // preserve the live stack when ref is already current, otherwise append.
  const chain = list.length && sameRef(list[list.length - 1], ref)
    ? list
    : [...list, ref]
  return chain.slice(-MAX_CARRY_DEPTH)
}

export function encodeCarryChain(chain) {
  const list = Array.isArray(chain) ? chain.filter(isValidRef) : []
  const bounded = list.slice(-MAX_CARRY_DEPTH)
  if (!bounded.length) return null
  try {
    return encodeURIComponent(JSON.stringify(bounded.map((ref) => ({ type: ref.type, id: ref.id, label: ref.label, path: ref.path }))))
  } catch {
    return null
  }
}

export function decodeCarryChain(raw) {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    const list = Array.isArray(parsed) ? parsed : (isValidRef(parsed) ? [parsed] : [])
    const bounded = list.filter(isValidRef).slice(-MAX_CARRY_DEPTH)
    return bounded.length ? bounded : null
  } catch {
    return null
  }
}

/**
 * Append an encoded return-to ref (or a bounded ancestor chain — see
 * buildCarryChain) onto a same-app destination URL. A no-op when the payload
 * is missing/invalid/empty or the URL already carries one (never double up —
 * the first ref wins). A bare ref keeps the original single-object encoding
 * (encodeCarryRef) so every existing single-ref caller is byte-for-byte
 * unchanged; an array uses the chain encoding.
 */
export function withCarryParam(url, refOrChain) {
  if (!url) return url
  const encoded = Array.isArray(refOrChain) ? encodeCarryChain(refOrChain) : encodeCarryRef(refOrChain)
  if (!encoded) return url
  if (url.includes(`${CARRY_PARAM}=`)) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}${CARRY_PARAM}=${encoded}`
}
